import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["convertToOptions"];
import { useMemo } from 'react';
import RoutableTeamText from '../components/RoutableTeamText';
import { useTeams } from './useTeams';
import { jsx as _jsx } from "react/jsx-runtime";
const checkHasChildTeams = childTeams => Array.isArray(childTeams) && Boolean(childTeams.length);
const convertTeamsToHierarchyOptions = teams => {
  const options = [];
  for (const team of teams) {
    const option = {
      dropdownText: /*#__PURE__*/_jsx(RoutableTeamText, {
        team: team
      }),
      text: team.name,
      value: team.id
    };
    if (checkHasChildTeams(team.childTeams)) {
      option.options = convertTeamsToHierarchyOptions(team.childTeams);
    }
    options.push(option);
  }
  return options;
};
const checkHasRoutableUsers = team => typeof team.numRoutableUsersInHierarchy === 'number' && team.numRoutableUsersInHierarchy > 0;
const getRoutableTeams = teams => {
  const routableTeams = [];
  for (const team of teams) {
    const hasRoutableUsers = checkHasRoutableUsers(team);
    if (hasRoutableUsers) {
      if (team.childTeams.length === 0) {
        routableTeams.push(team);
      } else {
        routableTeams.push(Object.assign({}, team, {
          childTeams: getRoutableTeams(team.childTeams)
        }));
      }
    }
  }
  return routableTeams;
};
function checkIsTeamOptionsProps(workspaceIdOrProps) {
  return !!workspaceIdOrProps && typeof workspaceIdOrProps !== 'number' && (!!workspaceIdOrProps.workspaceId || !!workspaceIdOrProps.objectTypeId);
}
function getUseTeamProps(workspaceIdOrProps, skipFetch, convertToOptions) {
  if (checkIsTeamOptionsProps(workspaceIdOrProps)) {
    return workspaceIdOrProps;
  }

  // if for any reason an empty object is passed in, we should treat it as a missing workspaceId
  const workspaceId = !workspaceIdOrProps || typeof workspaceIdOrProps === 'number' ? workspaceIdOrProps : null;
  return {
    workspaceId,
    skipFetch,
    convertToOptions
  };
}

// `Options` could be any value type, by default it's an array but it could also be something like an `Immutable.List`
export function useTeamOptions(workspaceIdOrProps, skipFetch, maybeConvertToOptions) {
  const _getUseTeamProps = getUseTeamProps(workspaceIdOrProps, skipFetch, maybeConvertToOptions),
    {
      convertToOptions
    } = _getUseTeamProps,
    teamsProps = _objectWithoutPropertiesLoose(_getUseTeamProps, _excluded);
  const {
    error,
    loading,
    refetch,
    refetching,
    teams
  } = useTeams(teamsProps);
  const teamOptions = useMemo(() => {
    if (teams && Array.isArray(teams)) {
      const convert = convertToOptions || convertTeamsToHierarchyOptions;
      return convert(getRoutableTeams(teams));
    }
    return [];
    //If we don't load anything and the workspaceId doesn't change, don't change teamOptions
    //When we make a call, we'll first be loading, and then not be loading, so we will still calculate with the new data since loading will
    //force a recalculation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsProps.objectTypeId, teamsProps.workspaceId, loading, convertToOptions]);
  return {
    data: teams || [],
    error,
    loading,
    refetch,
    refetching,
    teams: teamOptions
  };
}